import React from 'react'
import classnames from 'classnames'

import Button from '../../Button/Button'
import TableErrorBoundary from '../Features/ErrorBoundary'
import { getColumnStyle, isColumnFreezed } from '../TableHelper'

import { getFreezeColumnCount, getMinMaxStyle, getHeaderValue } from '../util'
import { isEnterPressed } from 'utils/keyboardUtils'

/**
 * It renders table header data
 * And handle sorting of column
 **/
export const TableHead = (props) => {
  let tableBody: HTMLElement = document.querySelector('.Table__body')
  let scrollBarWidth = tableBody != null ? tableBody.offsetWidth - tableBody.clientWidth : 0
  const {
    columnWidthsObjectState,
    columnWidthsObjectProp,
    equalWidthColumns,
    listData,
    isRowSelect,
    tableWidthSmaller
  } = props
  return (
    <div
      className={`Table__head ${props.isTableWidthGreater ? 'Table__head--scroll ' : ''} ${props.viewBy === 'Compact' ? 'Table__head--compact' : ''
        }`}
      ref={props.tableHeadRef}
      style={props.isResizable || props.v2Features.tableRowAction || props.canFreezeCheckbox ? {} : { paddingRight: scrollBarWidth + 'px' }}>
      {props.headerGroups.map((headerGroup: any) => (
        <div
          role="row"
          {...headerGroup.getHeaderGroupProps()}
          className={classnames(
            'Table__head__row',
            props.equalWidthColumns && 'Table__head__row--equal-width-columns',
            props.v2Features.tableRowAction && 'Table__head__row_action',
            tableWidthSmaller && 'Table__head_rowwidth'
          )}>
          {headerGroup.headers.map((column: any, index) => {
            let columnHeaderProps = column.getHeaderProps()

            if (props.isResizable) {
              columnHeaderProps = column.getHeaderProps({
                style: getMinMaxStyle(props.tableWidth)
              })
            }

            let AllSortByHeaderProps = {
              ...column.getHeaderProps([
                column.getSortByToggleProps(),
                column.decFirst
                  ? {
                    onClick: (e) => {
                      // Custom sort order: On first click, sort descending
                      const descFirstToggle = !column.isSorted
                        ? true // If not sorted, start with descending order
                        : !column.isSortedDesc // Otherwise, toggle normally

                      column.toggleSortBy(descFirstToggle)
                    }
                  }
                  : {},
                { style: { display: 'inline' }, role: 'presentation' }
              ])
            }
            let { title, key, ...sortByHeaderProps } = AllSortByHeaderProps
            const { style, ...headerPropsWithoutStyle }: any = sortByHeaderProps

            let columnHeaderStyle = getColumnStyle({
              column,
              columns: props.columns,
              cellProps: columnHeaderProps,
              columnWidthsObjectState,
              columnWidthsObjectProp,
              equalWidthColumns,
              isResizable: props.isResizable,
              totalCounts: props.totalCounts,
              tableRowAction: props.v2Features.tableRowAction
            })
            let freezeColumnCount = 0
            let isColumnFreezedResponse
            if (props.canFreezeColumn) {
              freezeColumnCount = getFreezeColumnCount(listData, isRowSelect)
              isColumnFreezedResponse = isColumnFreezed(freezeColumnCount, index)
              if (index > 0 && isColumnFreezedResponse) {
                columnHeaderStyle = {
                  ...columnHeaderStyle,
                  left: column.totalLeft
                }
              }
            }
            if (column.id === 'selection') {
              return (
                <div
                  {...column.getHeaderProps()}
                  className={
                    'Table-select-head flex-v-center' +
                    (isColumnFreezedResponse || props.canFreezeCheckbox ? ' freezed' : '')
                  }>
                  <TableErrorBoundary>{column.render('Header')}</TableErrorBoundary>
                </div>
              )
            }
            const { role, ...restColumnHeaderProps } = columnHeaderProps;

            const sortingData = props?.initialSortByV2?.id == column.id ? props?.initialSortByV2 : {}

            // let callBackForSortByV2 = {
            //   sortingDirection: column.decFirst ? sortingData?.sortingDirection === "desc" ? "asc" : "desc" : sortingData?.sortingDirection === "asc" ? "desc" : "asc",
            //   id: column?.id
            // };

            let nextDirection;
            if (column.decFirst) {
              if (!sortingData?.sortingDirection) {
                nextDirection = "desc";
              } else if (sortingData?.sortingDirection === "desc") {
                nextDirection = "asc"; // reset sorting on third click
              } else if (sortingData?.sortingDirection === "asc") {
                nextDirection = undefined;
              }
            }
            else if (!sortingData?.sortingDirection) {
              nextDirection = "asc"; // default to ascending on first click
            }
            else if (sortingData?.sortingDirection === "asc") {
              nextDirection = "desc";
            } else if (sortingData?.sortingDirection === "desc") {
              nextDirection = undefined; // reset sorting on third click
            }

            const callBackForSortByV2 = nextDirection
              ? {
                sortingDirection: nextDirection,
                id: column.id,
              }
              : {};

            if (props?.initialSortByV2 && props?.callBackForSortByV2 && !column.disableSortBy && sortByHeaderProps) {
              sortByHeaderProps = {
                ...sortByHeaderProps,
                onClick: (e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  props.callBackForSortByV2(callBackForSortByV2);
                }
              }
            }

            return (
              <div
                {...restColumnHeaderProps}
                role="columnheader"
                className={classnames(
                  'Table__head__column',
                  !!column.cssClass && column.cssClass,
                  column.isSorted && 'Table__head__cell--sorted',
                  ((props.isResizable && column.canResize) || !column.disableSortBy) && 'Table__head__column__hover',
                  isColumnFreezedResponse && 'freezed',
                  freezeColumnCount > 0 && freezeColumnCount === index + 1 && 'freezed_border',
                  column.id === 'tableActions' &&
                  props.v2Features.tableRowAction &&
                  'Table__head__cell--tableRowAction',
                  column.isResizing && 'isResizing'
                )}
                data-test-id={`${column.testId || 'cs-table-head'}-cell--${index}`}
                style={{ ...columnHeaderStyle }}>
                <span
                  className="Table__head__column-text"
                  {...sortByHeaderProps}
                  data-test-id={`${column.testId || 'cs-table-head'}-text--${index}`}>
                  <TableErrorBoundary>{column.render('Header')}</TableErrorBoundary>
                </span>
                {!props?.initialSortByV2 && !props?.callBackForSortByV2 && !column.disableSortBy && (
                  <Button
                    className={`sortingIcon-wrapper ${column.isSorted ? 'sorted' : 'not-sorted'}`}
                    {...headerPropsWithoutStyle}
                    testId={`${column.testId || 'cs-table-head-cell'}-sort--${index}`}
                    aria-label={`${getHeaderValue(column.Header)} sort ${!column.isSorted ? 'toggle' : column.isSortedDesc ? 'by descending' : 'by ascending'}`}
                    onKeyDown={(e: any) => {
                      if (isEnterPressed(e.key)) {
                        e.target.click()
                      }
                    }}
                    size="small"
                    buttonType="tertiary"
                    onlyIcon={true}
                    icon={column.isSorted ? (column.isSortedDesc ? "v2-SortingDesc" : 'v2-SortingAsc') : 'v2-NoSort'}
                    version="v2"
                    onlyIconHoverColor='secondary'
                  />
                )}

                {props?.initialSortByV2 && props?.callBackForSortByV2 && !column.disableSortBy && <Button
                  className={`sortingIcon-wrapper sortV2 ${sortingData?.sortingDirection === "asc" ? 'sorted' : 'not-sorted'}`}
                  size="small"
                  buttonType="tertiary"
                  onlyIcon={true}
                  version="v2"
                  icon={sortingData?.id ? (sortingData?.sortingDirection === "desc" ? "v2-SortingDesc" : 'v2-SortingAsc') : 'v2-NoSort'} //{column.isSorted ? (column.isSortedDesc ? "v2-SortingDesc" : 'v2-SortingAsc') : 'v2-NoSort'}
                  onlyIconHoverColor='secondary'
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    props.callBackForSortByV2(callBackForSortByV2)
                  }}
                />}

                {column.filter && <column.filter />}
                {props.isResizable &&
                  index >= 0 &&
                  column.canResize &&
                  props.totalCounts > 0 &&
                  !props.loading &&
                  column.id !== 'tableActions' && (
                    <span
                      data-test-id={`cs-table-head-resizer-${index}`}
                      {...column.getResizerProps()}
                      className={`resizer `}></span>
                  )}

              </div>
            )
          })}
        </div>
      ))
      }
    </div >
  )
}

export default TableHead